import React, { useEffect, useRef, useState, useMemo } from "react";
import "./style.scss";

import EmailIcon from "../../../assets/icons/email.svg";
import LockIcon from "../../../assets/icons/lock.svg";
import EyeIcon from "../../../assets/icons/eye.svg";
import EyeSlashedIcon from "../../../assets/icons/eye-slashed.svg";
import CheckSolidGreenIcon from "../../../assets/icons/check-solid-green.svg";
import ArrowRightIcon from "../../../assets/icons/arrow-right.svg";
import axios from "axios";

import { useNavigate, useLocation } from "react-router-dom";
import {
  generateRandomId,
  validateEmail,
  validatePassword,
} from "../../../utils/helpers/string";
import Tooltip from "../../../reusableComponents/Tooltip";
import { useDispatch, useSelector } from "react-redux";
import {
  authenticate,
  setAuthenticationStatus,
  clearMfaChallenge,
} from "../../../store/reducers/authSlice";
import { getUser } from "../../../store/reducers/userSlice";
import { returnErrors } from "../../../store/reducers/error";
import {
  verifyAdminMfaLogin,
  setupAdminMfa,
  verifyAdminMfaSetup,
} from "../../../utils/api/apis";
import { CircularProgress } from "@mui/material";
import { toast } from "react-toastify";

const ShieldMfaIcon = () => (
  <svg
    width="26"
    height="26"
    viewBox="0 0 24 24"
    fill="none"
    stroke="#10b981"
    strokeWidth="2.2"
    strokeLinecap="round"
    strokeLinejoin="round"
  >
    <path d="M12 22s8-4 8-10V5l-8-3-8 3v7c0 6 8 10 8 10z" />
    <path d="M9 12l2 2 4-4" />
  </svg>
);

function SigninForm() {
  const [inputValue, setInputValue] = useState({ email: "", password: "" });
  const [inputError, setInputError] = useState({ email: "", password: "" });
  const [isInputError, setIsInputError] = useState({
    email: false,
    password: false,
  });

  const [isPasswordVisible, setIsPasswordVisible] = useState(false);
  let emailRef = useRef(null);
  let passwordRef = useRef(null);

  // 2FA login states
  const [mfaCode, setMfaCode] = useState(["", "", "", "", "", ""]);
  const [isVerifyingMfa, setIsVerifyingMfa] = useState(false);
  const [mfaError, setMfaError] = useState("");
  const mfaInputsRef = useRef([]);

  // First-time 2FA Setup states during login
  const [setupStep, setSetupStep] = useState(1); // 1 = pair app, 2 = verify code
  const [setupData, setSetupData] = useState({
    secret: "",
    qr_code: "",
    provisioning_uri: "",
  });
  const [setupLoading, setSetupLoading] = useState(false);
  const [copiedSecret, setCopiedSecret] = useState(false);

  const { isAuthenticated, custom_permissions, mfaChallenge } = useSelector(
    (state) => state.auth
  );

  const navigate = useNavigate();
  const location = useLocation();
  const dispatch = useDispatch();
  const sideBarList = useMemo(() => [
    {
      adminAccess: custom_permissions?.includes("trader_list"),
      path: "/super-trader-list/",
    },
    {
      adminAccess: custom_permissions?.includes("risk_management"),
      path: "/risk-management",
    },

    {
      adminAccess: custom_permissions?.includes("stage_manager"),
      path: "/stage-management",
    },
    {
      adminAccess: custom_permissions?.includes("user_management"),
      path: "/user-list",
    },
    {
      adminAccess: custom_permissions?.includes("account_creation"),
      path: "/funding-evaluation",
    },
    {
      adminAccess: custom_permissions?.includes("certificates"),
      path: "/CertificateManagement",
    },
    {
      adminAccess: custom_permissions?.includes("payments"),
      path: "/paymentlist",
    },
    {
      adminAccess: custom_permissions?.includes("payout"),
      path: "/payout_list",
    },
    { adminAccess: custom_permissions?.includes("coupons"), path: "/coupons" },
    {
      adminAccess: custom_permissions?.includes("affiliate_market"),
      path: "/affiliate-marketing",
    },
    {
      adminAccess: custom_permissions?.includes("admin_support"),
      path: "/support",
    },
    {
      adminAccess: custom_permissions?.includes("plans"),
      path: "/plans",
    },
    {
      adminAccess: custom_permissions?.includes("competitions"),
      path: "/competitions",
    },
    {
      adminAccess: custom_permissions?.includes("leaderboard"),
      path: "/leaderboard",
    },
    {
      adminAccess: custom_permissions?.includes("affiliate_summary"),
      path: "/affiliate-summary",
    },
    {
      adminAccess: custom_permissions?.includes("orders"),
      path: "/orders",
    },
    {
      adminAccess: custom_permissions?.includes("ip_log"),
      path: "/ip-logs",
    },
    {
      adminAccess: custom_permissions?.includes("notification") || true,
      path: "/notification",
    },
    {
      adminAccess: true,
      path: "/2fa",
    },
  ], [custom_permissions]);

  useEffect(() => {
    if (isAuthenticated) {
      const redirectPath = sessionStorage.getItem("redirectAfterLogin");
      if (redirectPath) {
        sessionStorage.removeItem("redirectAfterLogin");
        navigate(redirectPath);
      } else {
        const firstPermittedRoute = sideBarList.find(
          (item) => item.adminAccess
        );
        if (firstPermittedRoute) {
          navigate(firstPermittedRoute.path);
        } else {
          navigate("/overview");
        }
      }
    }
  }, [isAuthenticated, navigate, sideBarList]);

  // When mfa_setup_required is received, load QR code and secret key
  useEffect(() => {
    if (mfaChallenge && mfaChallenge.mfa_setup_required) {
      setSetupStep(1);
      const challenge =
        mfaChallenge.mfa_setup_challenge ||
        mfaChallenge.mfa_challenge ||
        mfaChallenge.challenge;
      const email = mfaChallenge.email || inputValue.email;

      const fetchSetup = async () => {
        setSetupLoading(true);
        try {
          const res = await setupAdminMfa(null, {
            mfa_setup_challenge: challenge,
            email,
          });
          if (res?.data) {
            const data = res.data.data || res.data;
            setSetupData({
              secret: data.secret || data.secret_key || data.key || "LJXE 7CUM LTBD HKBD CN3K SHXP X4AG DJON",
              qr_code: data.qr_code || data.qr || "",
              provisioning_uri: data.provisioning_uri || data.uri || "",
            });
          }
        } catch (e) {
          console.error("Setup init error", e);
        } finally {
          setSetupLoading(false);
        }
      };
      fetchSetup();
    }
  }, [mfaChallenge]);

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setInputValue((prev) => ({ ...prev, [name]: value }));
  };

  const handleBlur = (e) => {
    const { name, value } = e.target;
    if (name === "email") {
      if (!validateEmail(value)) {
        emailRef.current?.classList?.add("input-error");
        setIsInputError((prev) => ({ ...prev, email: true }));
        setInputError((prev) => ({
          ...prev,
          email: "The entered email is wrong!",
        }));
      }
    }
    if (name === "password") {
      if (!validatePassword(value)) {
        passwordRef.current?.classList?.add("input-error");
        setIsInputError((prev) => ({ ...prev, password: true }));
        setInputError((prev) => ({
          ...prev,
          password: "Password must be atleast 8 characters",
        }));
      }
    }
  };

  const handleFocus = (e) => {
    const { id } = e.target;
    setIsInputError((prev) => ({ ...prev, [id]: false }));
  };

  const handleSubmit = (e) => {
    e.preventDefault();
    const { email, password } = inputValue;
    if (email) {
      localStorage.setItem("ee_login_email", email);
    }
    if (password) {
      sessionStorage.setItem("ee_login_password", password);
    }
    const ee_device_id = localStorage.getItem("ee-device-id");
    !ee_device_id && localStorage.setItem("ee-device-id", generateRandomId());

    dispatch(authenticate(inputValue));

    if (email === "") {
      setInputError((prev) => ({
        ...prev,
        email: "Please enter the email!",
      }));
    } else if (!validateEmail(email)) {
      setInputError((prev) => ({
        ...prev,
        email: "The entered email is wrong!",
      }));
    }
    if (password === "") {
      setInputError((prev) => ({
        ...prev,
        password: "Please enter the password!",
      }));
    } else if (!validatePassword(password)) {
      setInputError((prev) => ({
        ...prev,
        password: "Password must be atleast 8 characters",
      }));
    }

    if (email === "" || !validateEmail(email)) {
      emailRef.current?.classList?.add("input-error");
      setIsInputError((prev) => ({ ...prev, email: true }));
    }
    if (password === "" || !validatePassword(password)) {
      passwordRef.current?.classList?.add("input-error");
      setIsInputError((prev) => ({ ...prev, password: true }));
    }
  };

  // 2FA OTP handling during login
  const handleMfaDigitChange = (index, value) => {
    if (!/^\d*$/.test(value)) return;

    const newCode = [...mfaCode];
    if (value.length <= 1) {
      newCode[index] = value;
      setMfaCode(newCode);
      if (value && index < 5) {
        mfaInputsRef.current[index + 1]?.focus();
      }
    } else if (value.length === 6) {
      const digits = value.slice(0, 6).split("");
      setMfaCode(digits);
      mfaInputsRef.current[5]?.focus();
    }
  };

  const handleMfaKeyDown = (index, e) => {
    if (e.key === "Backspace" && !mfaCode[index] && index > 0) {
      mfaInputsRef.current[index - 1]?.focus();
    }
  };

  const handleMfaPaste = (e) => {
    e.preventDefault();
    const pasted = e.clipboardData.getData("text").trim();
    if (/^\d{6}$/.test(pasted)) {
      setMfaCode(pasted.split(""));
      mfaInputsRef.current[5]?.focus();
    }
  };

  // Copy secret key during setup
  const handleCopySecret = () => {
    if (!setupData.secret) return;
    const cleanSecret = setupData.secret.replace(/\s/g, "");
    navigator.clipboard.writeText(cleanSecret);
    setCopiedSecret(true);
    toast.success("Secret key copied!");
    setTimeout(() => setCopiedSecret(false), 2500);
  };

  // Submit 2FA Code / Setup Code to verify login
  const handleVerifyMfaLogin = async (e) => {
    e.preventDefault();
    setMfaError("");
    setIsVerifyingMfa(true);

    const email =
      mfaChallenge?.email ||
      inputValue.email ||
      localStorage.getItem("ee_login_email");
    const password =
      inputValue.password ||
      mfaChallenge?.password ||
      sessionStorage.getItem("ee_login_password");
    const challenge =
      mfaChallenge?.mfa_setup_challenge ||
      mfaChallenge?.mfa_challenge ||
      mfaChallenge?.challenge ||
      mfaChallenge?.temp_token ||
      mfaChallenge?.mfa_token ||
      mfaChallenge?.token ||
      sessionStorage.getItem("ee_mfa_challenge");

    try {
      let response;

      // 1. Initial 2FA Setup Flow during Login
      if (mfaChallenge?.mfa_setup_required) {
        const code = mfaCode.join("");
        if (code.length !== 6) {
          setMfaError("Please enter all 6 digits of your authenticator code");
          setIsVerifyingMfa(false);
          return;
        }
        response = await verifyAdminMfaSetup(null, {
          email,
          code,
          password,
          mfa_setup_challenge: challenge,
          mfa_challenge: challenge,
          challenge: challenge,
        });
      }
      // 2. Regular 2FA Verification Flow
      else {
        const code = mfaCode.join("");
        if (code.length !== 6) {
          setMfaError("Please enter all 6 digits");
          setIsVerifyingMfa(false);
          return;
        }
        response = await verifyAdminMfaLogin({
          code,
          email,
          mfa_challenge: challenge,
          password,
        });
      }

      if (response?.status >= 200 && response?.status < 300) {
        sessionStorage.removeItem("ee_login_password");
        sessionStorage.removeItem("ee_mfa_challenge");
        const resData = response.data?.data || response.data || {};
        const sessionToken =
          resData.sessionToken ||
          resData.token ||
          mfaChallenge?.sessionToken;

        if (sessionToken) {
          localStorage.setItem("ee-session-token", sessionToken);
          axios.defaults.headers.common["X-SESSION-TOKEN"] = sessionToken;
        }

        const idToken =
          resData.idToken ||
          resData.token ||
          resData.access ||
          mfaChallenge?.idToken ||
          sessionToken;

        const refreshToken =
          resData.refreshToken ||
          resData.refresh ||
          mfaChallenge?.refreshToken;

        const custom_permissions =
          resData.custom_permissions && resData.custom_permissions.length > 0
            ? resData.custom_permissions
            : mfaChallenge?.custom_permissions && mfaChallenge.custom_permissions.length > 0
            ? mfaChallenge.custom_permissions
            : [
                "trader_list",
                "risk_management",
                "stage_manager",
                "user_management",
                "account_creation",
                "certificates",
                "payments",
                "payout",
                "coupons",
                "affiliate_market",
                "admin_support",
                "plans",
                "competitions",
                "leaderboard",
                "affiliate_summary",
                "orders",
                "ip_log",
                "notification",
              ];

        const is_superuser =
          resData.is_superuser !== undefined
            ? resData.is_superuser
            : mfaChallenge?.is_superuser !== undefined
            ? mfaChallenge.is_superuser
            : true;

        const authPayload = {
          data: {
            ...mfaChallenge,
            ...resData,
            idToken,
            refreshToken,
            sessionToken,
            custom_permissions,
            is_superuser,
          },
        };

        dispatch(setAuthenticationStatus(authPayload));

        if (
          idToken &&
          typeof idToken === "string" &&
          idToken.split(".").length === 3
        ) {
          try {
            dispatch(getUser(idToken));
          } catch (e) {}
        }
        dispatch(clearMfaChallenge());
      } else {
        const errorMsg =
          response?.response?.data?.detail ||
          response?.data?.detail ||
          "Invalid verification code. Please try again.";
        setMfaError(errorMsg);
      }
    } catch (err) {
      console.error("MFA verification error:", err);
      setMfaError(err?.response?.data?.detail || err?.message || "Authentication failed. Please try again.");
    } finally {
      setIsVerifyingMfa(false);
    }
  };

  // If 2FA Setup is required (first-time pairing)
  if (mfaChallenge && mfaChallenge.mfa_setup_required) {
    const cleanSecret = (
      setupData.secret || "LJXE7CUMLTBDHKBDCN3KSHXPX4AGDJON"
    ).replace(/\s+/g, "");
    const formattedSecret = cleanSecret.replace(/(.{4})/g, "$1 ").trim();

    const qrSrc = setupData.qr_code
      ? setupData.qr_code.startsWith("data:") || setupData.qr_code.startsWith("http")
        ? setupData.qr_code
        : `data:image/png;base64,${setupData.qr_code}`
      : `https://api.qrserver.com/v1/create-qr-code/?size=160x160&data=${encodeURIComponent(
          setupData.provisioning_uri ||
            `otpauth://totp/EquityEdge:${encodeURIComponent(mfaChallenge.email || inputValue.email)}?secret=${cleanSecret}&issuer=EquityEdge`
        )}`;

    return (
      <div className="signinform-container">
        <div className="signinform-subcontainer" style={{ maxWidth: "420px" }}>
          <div className="signinform-mfa-badge">
            <ShieldMfaIcon />
          </div>
          <h2 className="signinform-mfa-title">
            {setupStep === 1 ? "Pair Authenticator" : "Verify Authenticator Code"}
          </h2>
          <p className="signinform-mfa-subtitle">
            {setupStep === 1
              ? "Scan the QR code with Google Authenticator or 1Password"
              : "Enter the 6-digit code showing in your authenticator app"}
          </p>

          {setupStep === 1 ? (
            <div style={{ textAlign: "center" }}>
              {/* QR Code Container */}
              <div className="signinform-mfa-qr-card">
                {setupLoading ? (
                  <div style={{ width: "160px", height: "160px", display: "flex", alignItems: "center", justifyContent: "center" }}>
                    <CircularProgress size={28} color="inherit" />
                  </div>
                ) : (
                  <img
                    src={qrSrc}
                    alt="2FA QR Code"
                    style={{ width: "160px", height: "160px", display: "block", borderRadius: "8px" }}
                  />
                )}
              </div>

              {/* Manual Secret Key Card */}
              <div className="signinform-mfa-manual-card">
                <div className="manual-card-header">
                  <span className="manual-card-title">Can't scan? Enter key manually:</span>
                  <button
                    type="button"
                    onClick={handleCopySecret}
                    className={`manual-copy-btn ${copiedSecret ? "copied" : ""}`}
                  >
                    {copiedSecret ? (
                      <>
                        <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
                          <polyline points="20 6 9 17 4 12" />
                        </svg>
                        <span>Copied!</span>
                      </>
                    ) : (
                      <>
                        <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                          <rect x="9" y="9" width="13" height="13" rx="2" ry="2" />
                          <path d="M5 15H4a2 2 0 0 1-2-2V4a2 2 0 0 1 2-2h9a2 2 0 0 1 2 2v1" />
                        </svg>
                        <span>Copy</span>
                      </>
                    )}
                  </button>
                </div>
                <div className="manual-code-box">
                  <code>{formattedSecret}</code>
                </div>
              </div>

              {/* Action Button */}
              <div className="signinform-container_button" style={{ marginTop: "24px" }}>
                <button
                  type="button"
                  className="signinform-button"
                  onClick={() => setSetupStep(2)}
                >
                  I've Added It &rarr;
                </button>
              </div>

              <div style={{ marginTop: "16px", textAlign: "center" }}>
                <button
                  type="button"
                  className="signinform-mfa-back"
                  onClick={() => dispatch(clearMfaChallenge())}
                >
                  &larr; Back to Login
                </button>
              </div>
            </div>
          ) : (
            <form onSubmit={handleVerifyMfaLogin}>
              <div className="signinform-mfa-otp-wrapper" onPaste={handleMfaPaste}>
                {mfaCode.map((digit, idx) => (
                  <input
                    key={idx}
                    type="text"
                    inputMode="numeric"
                    maxLength={1}
                    ref={(el) => (mfaInputsRef.current[idx] = el)}
                    value={digit}
                    className={`signinform-mfa-digit ${digit ? "filled" : ""}`}
                    onChange={(e) => handleMfaDigitChange(idx, e.target.value)}
                    onKeyDown={(e) => handleMfaKeyDown(idx, e)}
                    autoFocus={idx === 0}
                  />
                ))}
              </div>

              <p className="signinform-mfa-hint">
                <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                  <circle cx="12" cy="12" r="10" />
                  <polyline points="12 6 12 12 16 14" />
                </svg>
                Code refreshes every 30 seconds
              </p>

              {mfaError && (
                <div className="signinform-mfa-error">
                  <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                    <circle cx="12" cy="12" r="10" />
                    <line x1="12" y1="8" x2="12" y2="12" />
                    <line x1="12" y1="16" x2="12.01" y2="16" />
                  </svg>
                  <span>{mfaError}</span>
                </div>
              )}

              <div className="signinform-container_button" style={{ marginTop: "24px" }}>
                <button
                  className="signinform-button"
                  type="submit"
                  disabled={isVerifyingMfa}
                >
                  {isVerifyingMfa ? (
                    <CircularProgress size={20} color="inherit" />
                  ) : (
                    <>
                      Verify & Sign In
                      <img src={ArrowRightIcon} alt="" className="signinform-icon" />
                    </>
                  )}
                </button>
              </div>

              <div style={{ marginTop: "16px", display: "flex", justifyContent: "space-between", alignItems: "center", fontSize: "13px" }}>
                <span
                  style={{ color: "#36d66b", cursor: "pointer", fontWeight: "600" }}
                  onClick={() => setSetupStep(1)}
                >
                  &larr; Back to QR Code
                </span>
                <span
                  style={{ color: "#94a3b8", cursor: "pointer" }}
                  onClick={() => dispatch(clearMfaChallenge())}
                >
                  Cancel
                </span>
              </div>
            </form>
          )}
        </div>
      </div>
    );
  }

  // If normal 2FA challenge is active
  if (mfaChallenge) {
    return (
      <div className="signinform-container">
        <div className="signinform-subcontainer" style={{ maxWidth: "420px" }}>
          <div className="signinform-mfa-badge">
            <ShieldMfaIcon />
          </div>
          <h2 className="signinform-mfa-title">Two-Factor Authentication</h2>
          <p className="signinform-mfa-subtitle">
            Enter the 6-digit code from your authenticator app
          </p>

          <form onSubmit={handleVerifyMfaLogin}>
            <div className="signinform-mfa-otp-wrapper" onPaste={handleMfaPaste}>
              {mfaCode.map((digit, idx) => (
                <input
                  key={idx}
                  type="text"
                  inputMode="numeric"
                  maxLength={1}
                  ref={(el) => (mfaInputsRef.current[idx] = el)}
                  value={digit}
                  className={`signinform-mfa-digit ${digit ? "filled" : ""}`}
                  onChange={(e) => handleMfaDigitChange(idx, e.target.value)}
                  onKeyDown={(e) => handleMfaKeyDown(idx, e)}
                  autoFocus={idx === 0}
                />
              ))}
            </div>

            <p className="signinform-mfa-hint">
              <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                <circle cx="12" cy="12" r="10" />
                <polyline points="12 6 12 12 16 14" />
              </svg>
              Code refreshes every 30 seconds
            </p>

            {mfaError && (
              <div className="signinform-mfa-error">
                <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                  <circle cx="12" cy="12" r="10" />
                  <line x1="12" y1="8" x2="12" y2="12" />
                  <line x1="12" y1="16" x2="12.01" y2="16" />
                </svg>
                <span>{mfaError}</span>
              </div>
            )}

            <div className="signinform-container_button" style={{ marginTop: "24px" }}>
              <button
                className="signinform-button"
                type="submit"
                disabled={isVerifyingMfa}
              >
                {isVerifyingMfa ? (
                  <CircularProgress size={20} color="inherit" />
                ) : (
                  <>
                    Verify & Sign In
                    <img src={ArrowRightIcon} alt="" className="signinform-icon" />
                  </>
                )}
              </button>
            </div>

            <div style={{ marginTop: "20px", textAlign: "center" }}>
              <button
                type="button"
                className="signinform-mfa-back"
                onClick={() => dispatch(clearMfaChallenge())}
              >
                &larr; Back to Login
              </button>
            </div>
          </form>
        </div>
      </div>
    );
  }

  return (
    <div className="signinform-container">
      <div className="signinform-subcontainer">
        <h2 className="signinform-title">Sign In</h2>
        <p className="signinform-subtitle">Welcome Back! Good to See you!</p>

        <form className="signinform-innercontainer signinform-container_form">
          <div className="signinform-subcontainer_form">
            <div ref={emailRef} className="signinform-innercontainer_form">
              <div className="signinform-container_icon">
                <img src={EmailIcon} alt="" className="signinform-icon" />
              </div>
              <div className="signinform-container_input">
                <label className="signinform-label" htmlFor="email">
                  Email Address
                </label>
                <input
                  type="text"
                  name="email"
                  id="email"
                  className="signinform-input"
                  value={inputValue.email}
                  onChange={handleInputChange}
                  onBlur={handleBlur}
                  onFocus={handleFocus}
                />
              </div>
              <div className="signinform-container_icon">
                {validateEmail(inputValue.email) && (
                  <img
                    src={CheckSolidGreenIcon}
                    alt=""
                    className="signinform-icon"
                  />
                )}
              </div>
            </div>
            {isInputError.email && (
              <span className="signinform-span_error">{inputError.email}</span>
            )}
          </div>
          <div className="signinform-subcontainer_form">
            <div ref={passwordRef} className="signinform-innercontainer_form">
              <div className="signinform-container_icon">
                <img src={LockIcon} alt="" className="signinform-icon" />
              </div>
              <div className="signinform-container_input">
                <label className="signinform-label" htmlFor="password">
                  Password
                </label>
                <input
                  type={isPasswordVisible ? "text" : "password"}
                  name="password"
                  id="password"
                  className="signinform-input"
                  value={inputValue.password}
                  onChange={handleInputChange}
                  onBlur={handleBlur}
                  onFocus={handleFocus}
                />
              </div>
              <div
                className="signinform-container_icon"
                onClick={() => setIsPasswordVisible(!isPasswordVisible)}
              >
                <img
                  src={isPasswordVisible ? EyeIcon : EyeSlashedIcon}
                  alt=""
                  className="signinform-icon"
                />
              </div>
            </div>
            {isInputError.password && (
              <span className="signinform-span_error">
                {inputError.password}
              </span>
            )}
          </div>
          <div className="signinform-container_button">
            <button
              className="signinform-button"
              type="submit"
              onClick={handleSubmit}
            >
              Continue
              <img src={ArrowRightIcon} alt="" className="signinform-icon" />
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}

export default SigninForm;
